import { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AvatarVariant, ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { EyeVisible } from '@factorialco/factorial-one-react/icons/app'
import {
  FormFragment,
  IsFormsAbstractionEnabledDocument,
  QuestionnaireFragment,
  SurveysHeaderTrainingClassFragment,
  TrainingSurveyRespondentsFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { FormPreview } from 'modules/forms'
import { serializeQuestionnaireResponseContent } from 'modules/trainings/lib/trainingFormQuestions'

import { PreviewSidepanel } from '../Preview'

type Props = {
  form: FormFragment
  questionnaire: QuestionnaireFragment[] | undefined
  groups: SurveysHeaderTrainingClassFragment[]
  respondents: TrainingSurveyRespondentsFragment[] | []
  trainingStatus: string
}

export const TrainingFormHeader: FC<Props> = ({
  respondents,
  groups,
  questionnaire,
  form,
  trainingStatus,
}) => {
  const navigate = useNavigate()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const [openSidepanel, setOpenSidepanel] = useState<boolean>(false)
  const { id } = useParams()
  const sendSurvey = () => {
    navigate(
      navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.sendSurvey.fullPath({
        id: form.resourceId,
        formId: form.id,
      })
    )
  }
  const { data } = useQuery(IsFormsAbstractionEnabledDocument, {
    variables: { companyId: company.id },
  })
  const hasFormsFF = data?.features.featuresConnection.nodes[0]?.enabled ?? false
  const blockedSendModalNavigation = () => {
    navigate(
      navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.blockedSend.fullPath({
        id: form.resourceId,
        formId: form.id,
      })
    )
  }

  const respondentsListEffectiveness: AvatarVariant[] = respondents.length
    ? Array.from(
        new Map(
          respondents.map(({ respondent }) => [
            respondent?.id,
            {
              firstName: respondent?.firstName ?? '',
              lastName: respondent?.lastName ?? '',
              type: 'person' as const,
              src: buildUrl(respondent?.avatar ?? ''),
            },
          ])
        ).values()
      )
    : []

  const goToSettings = () => {
    navigate(
      navigation.trainings.list.details_revamp.trainingSurveyDetail.settings.fullPath({
        id: form.resourceId,
        formId: form.id,
      })
    )
  }

  const groupsMetadata = groups.map((group) => group.name)

  return (
    <>
      <ResourceHeader
        title={form.title}
        metadata={
          form.status === 'active'
            ? [
                form.formType === 'satisfaction' &&
                  !form.anonymous && {
                    label: i18n.t('trainings.surveys.header.metadata.participants'),
                    value: {
                      type: 'list',
                      variant: 'person',
                      avatars: respondentsListEffectiveness,
                    },
                  },
                form.formType === 'effectiveness' && {
                  label: i18n.t('trainings.surveys.header.metadata.respondent'),
                  value: {
                    type: 'list',
                    variant: 'person',
                    avatars: respondentsListEffectiveness,
                  },
                },
                {
                  label: i18n.t('trainings.surveys.header.metadata.groups'),
                  value: {
                    type: 'data-list',
                    data: groupsMetadata,
                  },
                },
              ]
            : []
        }
        description={
          form.formType === 'satisfaction'
            ? i18n.t('trainings.surveys.header.description.satisfaction')
            : i18n.t('trainings.surveys.header.description.effectiveness')
        }
        status={{
          label: '',
          text:
            form.status === 'active'
              ? i18n.t('trainings.surveys.status.sent')
              : i18n.t('trainings.surveys.status.draft'),
          variant: form.status === 'active' ? 'positive' : 'neutral',
        }}
        primaryAction={{
          disabled: trainingStatus !== 'active',
          label: i18n.t('common.send'),
          onClick: trainingStatus === 'active' ? sendSurvey : blockedSendModalNavigation,
        }}
        secondaryActions={[
          {
            icon: EyeVisible,
            label: i18n.t('trainings.surveys.create.preview'),
            onClick: () => setOpenSidepanel(true),
          },
        ].filter(Boolean)}
        otherActions={[
          {
            label: i18n.t('trainings.surveys.create.dropdown.edit'),
            onClick: goToSettings,
          },
          form.status === 'draft' && {
            label: i18n.t('trainings.trainings_table.delete'),
            onClick: () => {
              navigate(
                navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.delete.fullPath(
                  {
                    id: form.resourceId,
                    formId: form.id,
                  }
                )
              )
            },
          },
        ]}
      />

      {hasFormsFF ? (
        <FormPreview
          isOpen={openSidepanel}
          onClose={() => setOpenSidepanel(false)}
          formId={form.id}
          resourceId={id}
          resourceType="Trainings::Training"
          resourceHeaderModule={{ href: '/trainings', id: 'my_trainings', label: 'Trainings' }}
        />
      ) : (
        questionnaire && (
          <PreviewSidepanel
            open={openSidepanel}
            onClose={() => setOpenSidepanel(!open)}
            currentPreview={serializeQuestionnaireResponseContent(questionnaire)}
          />
        )
      )}
    </>
  )
}
